import type React from 'react';
import type { FormControlAriaProps } from './FormField';

interface EnumSelectProps<T extends number, TValue extends T | null> extends FormControlAriaProps {
    value: TValue;
    options: T[];
    getLabel: (option: T) => string;
    onChange: (value: TValue) => void;
    // Shown as the blank option; a null value always renders a blank option so the select can display it
    emptyLabel?: string;
    id?: string;
    disabled?: boolean;
    ariaLabel?: string;
    className?: string;
}

const EnumSelect = <T extends number, TValue extends T | null>({
    value,
    options,
    getLabel,
    onChange,
    emptyLabel,
    id,
    disabled = false,
    ariaLabel,
    // dropdown-field opts the select into the label spacing Spiris gives inputs (it only styles .dropdown-toggle buttons), so selects line up with them
    className = 'form-control dropdown-field',
    ...ariaProps
}: EnumSelectProps<T, TValue>): React.JSX.Element => (
    <select
        id={id}
        className={className}
        disabled={disabled}
        aria-label={ariaLabel}
        value={value ?? ''}
        onChange={(e) => onChange((e.target.value === '' ? null : Number(e.target.value)) as TValue)}
        {...ariaProps}
    >
        {(emptyLabel !== undefined || value === null) && <option value="">{emptyLabel ?? ''}</option>}
        {options.map((option) => (
            <option key={option} value={option}>
                {getLabel(option)}
            </option>
        ))}
    </select>
);

export default EnumSelect;
